/**
 * Kiosk Fragrance Quiz Web Component
 *
 * Mall-kiosk variant of the fragrance quiz. Differs from the main quiz in:
 *  - No email capture (kiosk has no customer flow).
 *  - No add-to-cart / bundle picker. Staff handles fulfilment in person.
 *  - Results are display-only: card per recommended fragrance with notes,
 *    so staff can fetch them from behind the counter.
 *  - Storage keys are namespaced (`theme:kiosk-quiz-*`) so this quiz
 *    doesn't share progress/state with the regular online quiz.
 */
class KioskFragranceQuiz extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 0;
    this.totalQuestions = 5;
    this.answers = {};
    this.products = [];
    this.recommendations = [];
  }

  connectedCallback() {
    // Check if we should skip fullscreen (user closed quiz previously)
    const skipFullscreen = sessionStorage.getItem('theme:kiosk-quiz-skip-fullscreen');
    if (skipFullscreen) {
      sessionStorage.removeItem('theme:kiosk-quiz-skip-fullscreen');
    } else {
      this.enableFullScreen();
    }

    // Parse product data
    const productDataEl = this.querySelector('[data-quiz-products]');
    if (productDataEl) {
      try {
        this.products = JSON.parse(productDataEl.textContent);
      } catch (e) {
        console.error('Failed to parse kiosk quiz products:', e);
        this.products = [];
      }
    }

    // Get settings from attributes
    this.resultsCount = parseInt(this.dataset.resultsCount) || 4;

    // Cache DOM elements
    this.screens = this.querySelectorAll('[data-screen]');
    this.progressFill = this.querySelector('[data-progress-fill]');
    this.progressText = this.querySelector('[data-progress-text]');
    this.resultsListEl = this.querySelector('[data-quiz-results-list]');

    this.initializeEventListeners();

    // Restore saved progress
    this.restoreProgress();

    // Show current screen
    this.showScreen(this.currentStep);
  }

  saveProgress() {
    const progressData = {
      currentStep: this.currentStep,
      answers: this.answers,
      timestamp: Date.now()
    };
    sessionStorage.setItem('theme:kiosk-quiz-progress', JSON.stringify(progressData));
  }

  restoreProgress() {
    const saved = sessionStorage.getItem('theme:kiosk-quiz-progress');
    if (!saved) return;

    try {
      const data = JSON.parse(saved);

      // Only restore if less than 15 minutes old (kiosk = fast turnover)
      if (data.timestamp && (Date.now() - data.timestamp) > 15 * 60 * 1000) {
        sessionStorage.removeItem('theme:kiosk-quiz-progress');
        return;
      }

      this.currentStep = data.currentStep || 0;
      this.answers = data.answers || {};

      // Restore visual selections
      Object.entries(this.answers).forEach(([questionId, answer]) => {
        const screen = this.querySelector(`[data-question-id="${questionId}"]`);
        if (screen) {
          const option = screen.querySelector(`[data-answer="${answer.value}"]`);
          if (option) {
            option.classList.add('quiz__option--selected');
          }
        }
      });
    } catch (e) {
      console.error('Error restoring kiosk quiz progress:', e);
      sessionStorage.removeItem('theme:kiosk-quiz-progress');
    }
  }

  initializeEventListeners() {
    // Option clicks
    this.querySelectorAll('.quiz__option').forEach(option => {
      option.addEventListener('click', (e) => this.handleOptionClick(e));
    });

    // Back buttons
    this.querySelectorAll('[data-quiz-back]').forEach(btn => {
      btn.addEventListener('click', () => this.goBack());
    });

    // Retake quiz
    const retakeBtn = this.querySelector('[data-quiz-retake]');
    if (retakeBtn) {
      retakeBtn.addEventListener('click', () => this.retakeQuiz());
    }

    // Close button - on a kiosk this just restarts the experience
    const closeBtn = this.querySelector('[data-quiz-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.retakeQuiz());
    }
  }

  handleOptionClick(e) {
    const option = e.currentTarget;
    const screen = option.closest('[data-screen]');
    const questionId = screen.dataset.questionId;
    const answer = option.dataset.answer;
    const tags = option.dataset.tags ? option.dataset.tags.split(',').map(t => t.trim()) : [];

    this.answers[questionId] = {
      value: answer,
      tags: tags
    };

    // Visual feedback
    screen.querySelectorAll('.quiz__option').forEach(opt => {
      opt.classList.remove('quiz__option--selected');
    });
    option.classList.add('quiz__option--selected');

    this.saveProgress();

    // Auto-advance after short delay
    setTimeout(() => {
      this.goNext();
    }, 300);
  }

  goNext() {
    if (this.currentStep < this.totalQuestions - 1) {
      this.currentStep++;
      this.saveProgress();
      this.showScreen(this.currentStep);
    } else if (this.currentStep === this.totalQuestions - 1) {
      // Last question done — go straight to results (no email step)
      this.calculateRecommendations();
      this.showLoadingScreen();
      // Brief loading flourish so it feels deliberate
      setTimeout(() => this.showResults(), 600);
    }
  }

  goBack() {
    if (this.currentStep > 0) {
      this.currentStep--;
      if (this.currentStep >= this.totalQuestions) {
        this.currentStep = this.totalQuestions - 1;
      }
      this.saveProgress();
      this.showScreen(this.currentStep);
    }
  }

  showScreen(index) {
    this.screens.forEach(screen => {
      screen.style.display = 'none';
    });

    const targetScreen = this.querySelector(`[data-screen="${index}"]`);
    if (targetScreen) {
      targetScreen.style.display = 'block';
      targetScreen.classList.remove('quiz__screen--animate-in');
      void targetScreen.offsetWidth; // Force reflow
      targetScreen.classList.add('quiz__screen--animate-in');
    }

    this.scrollToQuiz();
    this.updateProgress();
  }

  scrollToQuiz() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  enableFullScreen() {
    this.hiddenElements = [];

    const elementsToHide = [
      '#shopify-section-header',
      '#shopify-section-footer',
      '#shopify-section-announcement-bar',
      '.shopify-section--header',
      '.shopify-section--footer',
      '.shopify-section--announcement-bar',
      'header.header',
      'footer.footer',
      '.announcement-bar'
    ];

    elementsToHide.forEach(selector => {
      const el = document.querySelector(selector);
      if (el) {
        this.hiddenElements.push({ el, originalDisplay: el.style.display });
        el.style.display = 'none';
      }
    });

    document.body.classList.add('kiosk-quiz-fullscreen');
  }

  disableFullScreen() {
    if (this.hiddenElements) {
      this.hiddenElements.forEach(({ el, originalDisplay }) => {
        el.style.display = originalDisplay || '';
      });
    }
    document.body.classList.remove('kiosk-quiz-fullscreen');
  }

  updateProgress() {
    if (!this.progressFill || !this.progressText) return;

    const progressEl = this.querySelector('[data-quiz-progress]');

    if (this.currentStep >= this.totalQuestions) {
      if (progressEl) progressEl.style.display = 'none';
      return;
    }

    if (progressEl) progressEl.style.display = 'block';

    const progress = ((this.currentStep + 1) / this.totalQuestions) * 100;
    this.progressFill.style.width = `${progress}%`;
    this.progressText.textContent = `Question ${this.currentStep + 1} of ${this.totalQuestions}`;
  }

  showLoadingScreen() {
    this.screens.forEach(screen => {
      screen.style.display = 'none';
    });
    const loadingScreen = this.querySelector('[data-screen="loading"]');
    if (loadingScreen) {
      loadingScreen.style.display = 'block';
    }
  }

  showResults() {
    sessionStorage.removeItem('theme:kiosk-quiz-progress');

    this.renderResults();

    // Show results screen (index 5 since email screen is removed)
    this.currentStep = 5;
    this.showScreen(5);
  }

  renderResults() {
    if (!this.resultsListEl) return;

    if (!this.recommendations.length) {
      this.resultsListEl.innerHTML = '<p class="quiz__subtitle">No matches found. Please retake the quiz or ask a staff member for help.</p>';
      return;
    }

    const cards = this.recommendations.map((rec, idx) => this.buildResultCard(rec, idx + 1));
    this.resultsListEl.innerHTML = cards.join('');
  }

  buildResultCard(product, rank) {
    if (!product) return '';

    const escape = (s) => String(s || '').replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));

    const title = escape(product.title);
    const image = product.image ? `style="background-image:url('${escape(product.image)}')"` : '';
    const gender = escape(product.gender || '');

    const noteRow = (label, value) => {
      if (!value || !value.trim()) return '';
      return `
        <div class="quiz__result-note-row">
          <span class="quiz__result-note-label">${label}</span>
          <span class="quiz__result-note-value">${escape(value)}</span>
        </div>
      `;
    };

    const notesHtml = [
      noteRow('Top', product.topNotes),
      noteRow('Heart', product.middleNotes),
      noteRow('Base', product.baseNotes)
    ].join('');

    return `
      <article class="quiz__result-card">
        <span class="quiz__result-rank">Pick #${rank}</span>
        <div class="quiz__result-image" ${image}></div>
        <h3 class="quiz__result-title">${title}</h3>
        ${gender ? `<span class="quiz__result-gender">${gender}</span>` : ''}
        ${notesHtml ? `<div class="quiz__result-notes">${notesHtml}</div>` : ''}
      </article>
    `;
  }

  /**
   * Maps abstract quiz tags to actual scent note names from the product catalogue.
   * Identical to the main quiz — these mappings are based on the fragrance catalogue,
   * not the channel.
   */
  static SCENT_FAMILIES = {
    // Fresh / Clean family
    fresh:   ['bergamot','lemon','grapefruit','mandarin','orange','lime','petitgrain','neroli','green','mint','peppermint','marine','lavender','tea','freesia','green tea','green notes'],
    clean:   ['musk','white musk','lavender','neroli','bergamot','lemon','freesia','lily-of-the-valley','muguet','tea','green tea','bamboo','white rose'],
    citrus:  ['bergamot','lemon','grapefruit','mandarin','orange','lime','tangerine','yuzu','clementine','blood orange','petitgrain','neroli','citrus','citron','sicilian lemon','amalfi lemon'],
    aquatic: ['marine','sea water','marine accord','marine notes','bergamot','grapefruit','mint','violet leaf','violet leaves'],
    green:   ['green','violet leaf','violet leaves','galbanum','basil','thyme','rosemary','fig','juniper','pine needles','moss','vetiver','clary sage','bamboo','green notes','green leaves'],
    light:   ['bergamot','neroli','freesia','lily-of-the-valley','muguet','musk','white musk','peony','tea','lemon','mandarin','green tea'],

    // Warm / Sweet family
    warm:    ['vanilla','amber','tonka bean','tonka','cinnamon','benzoin','caramel','honey','sandalwood','cashmeran','ambergris','myrrh','labdanum','peru balsam'],
    vanilla: ['vanilla','tonka bean','tonka','caramel','marshmallow','praline','cream','whipped cream','coconut','cocoa','chocolate','cotton candy','bourbon vanilla','vanilla bean','madagascar vanilla'],
    amber:   ['amber','ambergris','ambroxan','labdanum','benzoin','olibanum','frankincense','incense','amber gris'],
    sweet:   ['vanilla','caramel','honey','praline','marshmallow','sugar','chocolate','cocoa','tonka bean','cream','whipped cream','cotton candy','turkish delight','lychee','peach','apricot','pear','cherry','plum','raspberry','blackberry','strawberry','fig','apple','rock sugar'],
    gourmand:['vanilla','caramel','chocolate','cocoa','coffee','praline','marshmallow','honey','cream','sugar','tonka bean','almond','hazelnut','pistachio','coconut','rum','cognac','cotton candy','turkish delight','salted caramel'],

    // Woody / Earthy family
    woody:   ['cedar','cedarwood','sandalwood','vetiver','patchouli','guaiac wood','oud','birch','oak','rosewood','gaiac wood','virginia cedar','papyrus wood'],
    earthy:  ['vetiver','patchouli','oakmoss','moss','myrrh','incense','tobacco','leather','suede','birch','truffle'],
    moss:    ['oakmoss','moss','patchouli','vetiver','green'],

    // Floral family
    floral:  ['rose','jasmine','tuberose','iris','peony','lily','orchid','gardenia','magnolia','freesia','orange blossom','ylang-ylang','neroli','violet','carnation','osmanthus','heliotrope','mimosa','geranium','narcissus','wildflowers','lily of the valley','lily-of-the-valley','may rose','bulgarian rose','turkish rose','white peony','spider lily'],
    powdery: ['iris','orris','violet','heliotrope','musk','peony','lily-of-the-valley','muguet','almond','orris root'],

    // Bold / Spicy family
    bold:    ['oud','leather','saffron','pepper','black pepper','pink pepper','incense','tobacco','cinnamon','truffle','labdanum','clove','cloves','nutmeg'],
    spicy:   ['pepper','black pepper','pink pepper','cinnamon','cardamom','saffron','clove','cloves','nutmeg','ginger','cumin','coriander','chilli pepper','anise','szechuan pepper'],
    oud:     ['oud','oud wood','saffron','incense','myrrh','frankincense','olibanum','labdanum','leather','amber'],
    leather: ['leather','suede','birch','tobacco','labdanum','oud','vetiver','dark leather'],
    powerful:['oud','leather','saffron','tobacco','incense','amber','ambergris','patchouli','vetiver','labdanum','musk','pepper'],

    // Mysterious / Dark family
    mysterious:['oud','incense','saffron','myrrh','frankincense','olibanum','truffle','leather','labdanum','patchouli','amber','plum'],
    dark:    ['oud','leather','tobacco','incense','truffle','coffee','cocoa','chocolate','plum','saffron','dark woods','dark chocolate'],
    smoky:   ['incense','tobacco','oud','guaiac wood','birch','vetiver','labdanum','myrrh','leather','tar'],
    incense: ['incense','frankincense','olibanum','myrrh','labdanum','oud','saffron','benzoin'],

    // Intensity tags
    soft:        ['musk','white musk','peony','freesia','lily-of-the-valley','iris','neroli','bergamot','tea','green tea'],
    intimate:    ['musk','vanilla','sandalwood','amber','cashmeran','cream','iris','orris'],
    'skin-scent':['musk','sandalwood','ambroxan','cashmeran','cedarwood','iris'],
    moderate:    ['bergamot','lavender','geranium','rose','cedar','cedarwood','sandalwood','musk','vetiver','patchouli','tonka bean'],
    balanced:    ['bergamot','lavender','geranium','rose','cedar','cedarwood','sandalwood','musk','vetiver','tonka bean','vanilla'],
    versatile:   ['bergamot','lavender','cedar','cedarwood','sandalwood','musk','vetiver','geranium','rose','pepper','vanilla','ambroxan'],
    strong:      ['oud','leather','tobacco','saffron','incense','amber','patchouli','vanilla','pepper','cinnamon','clove'],
    projection:  ['oud','amber','ambergris','saffron','incense','patchouli','vanilla','tonka bean','leather','tobacco','musk','vetiver'],
    sillage:     ['oud','amber','saffron','incense','patchouli','vanilla','tonka bean','ambergris','leather','musk','labdanum','benzoin'],

    // Gender tags
    masculine: ['bergamot','vetiver','leather','tobacco','oud','pepper','lavender','cedar','cedarwood','patchouli','mint','grapefruit','marine','oakmoss','incense'],
    feminine:  ['rose','jasmine','tuberose','peony','iris','freesia','gardenia','magnolia','vanilla','caramel','marshmallow','cherry','raspberry','lychee','pear','orchid','ylang-ylang'],
    unisex:    ['bergamot','sandalwood','vanilla','cedar','cedarwood','musk','ambergris','saffron','oud','patchouli','amber','vetiver','rose','incense','tonka bean','pepper']
  };

  countNoteKeywords(product) {
    const allNotes = [
      product.topNotes || '',
      product.middleNotes || '',
      product.baseNotes || ''
    ].join(', ');

    return allNotes.split(',').filter(n => n.trim().length > 0).length;
  }

  scoreProduct(product) {
    const allNotes = [
      product.topNotes || '',
      product.middleNotes || '',
      product.baseNotes || ''
    ].join(', ').toLowerCase();

    if (!allNotes.trim()) return 0;

    const answerTags = [];
    Object.entries(this.answers).forEach(([questionId, answer]) => {
      if (questionId === 'gender') return;
      if (answer.tags && answer.tags.length) {
        answerTags.push(...answer.tags);
      }
    });

    if (answerTags.length === 0) return 0;

    let rawScore = 0;
    const matchedIngredients = new Set();

    answerTags.forEach(tag => {
      const tagLower = tag.toLowerCase().trim();
      const ingredients = KioskFragranceQuiz.SCENT_FAMILIES[tagLower];
      if (!ingredients) return;

      ingredients.forEach(ingredient => {
        if (matchedIngredients.has(ingredient)) return;
        if (allNotes.includes(ingredient.toLowerCase())) {
          matchedIngredients.add(ingredient);
          rawScore += 1;
        }
      });
    });

    const noteCount = this.countNoteKeywords(product);
    const normalized = noteCount > 0 ? rawScore / Math.sqrt(noteCount) : 0;
    return normalized;
  }

  calculateRecommendations() {
    // 1. Filter by gender (hard filter from Q1)
    const genderAnswer = this.answers.gender?.value || 'unisex';
    const genderFiltered = this.products.filter(product => product.gender === genderAnswer);

    // 2. Score against quiz answers, with small jitter to keep results feeling fresh
    const scored = genderFiltered.map(product => {
      const baseScore = this.scoreProduct(product);
      const jitter = 0.85 + (Math.random() * 0.30);
      return {
        ...product,
        quizScore: baseScore,
        sortScore: baseScore * jitter
      };
    });

    // 3. Sort by jittered score (desc), then sales rank (asc) as tiebreaker
    scored.sort((a, b) => {
      if (b.sortScore !== a.sortScore) return b.sortScore - a.sortScore;
      return (a.salesRank || 9999) - (b.salesRank || 9999);
    });

    // 4. Take top N
    this.recommendations = scored.slice(0, this.resultsCount);

    // Backfill if not enough gender matches
    if (this.recommendations.length < this.resultsCount) {
      const remaining = scored
        .filter(p => !this.recommendations.find(r => r.id === p.id))
        .sort((a, b) => (a.salesRank || 9999) - (b.salesRank || 9999));

      while (this.recommendations.length < this.resultsCount && remaining.length > 0) {
        this.recommendations.push(remaining.shift());
      }
    }
  }

  retakeQuiz() {
    this.currentStep = 0;
    this.answers = {};
    this.recommendations = [];

    sessionStorage.removeItem('theme:kiosk-quiz-progress');

    this.querySelectorAll('.quiz__option--selected').forEach(opt => {
      opt.classList.remove('quiz__option--selected');
    });

    if (this.resultsListEl) {
      this.resultsListEl.innerHTML = '';
    }

    this.showScreen(0);
  }
}

if (!customElements.get('kiosk-fragrance-quiz')) {
  customElements.define('kiosk-fragrance-quiz', KioskFragranceQuiz);
}
